import React from 'react'
// libs
import { reduxForm } from 'redux-form'
import { useSnackbar } from 'notistack'
import { useDispatch } from 'react-redux'
// store
import { setCurrentUser } from 'store/reducers/currentUser'
// hooks
import { useFileUpload } from 'hooks/useFileUpload'
import { useCurrentUser, useShowModal } from 'hooks/index'
// components
import Grid from '@mui/material/Grid2'
import UserData from './components/UserData'
import ImageCropper from 'components/Modals/ImageCropperModal'
import SecurityData from './components/SecurityData'
import OrganizationData from './components/OrganizationData'
import TwoFactorSettings from './components/TwoFactorSettings'
import TokenConfiguration from './components/TokenConfiguration'
import PasswordChangeModal from 'components/Modals/PasswordChangeModal'
import { Button, BUTTON_LAYOUTS } from 'components/UI'
// constants
import { Forms, Modals } from 'constants/index'
// types
import { AssetType, UserRoles } from 'types/graphql'
import { InjectedFormProps } from 'redux-form/lib/reduxForm'
// views
import { BottomPaper, Form, FormInnerWrapper } from './views'
// utils
import { omit } from 'lodash'
import { getFileParams } from 'utils/upload'
import { getSnackbarCloseButton } from 'providers/SnackbarProvider/utils'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'

interface Props {
  updateUser: (data: any) => Promise<void>
  onCancelClick: () => void
}

type Avatar = { blob: Blob; url: string; type: string; changed: boolean }

const UserInformationForm: React.FC<InjectedFormProps<any, Props> & Props> = ({
  updateUser,
  handleSubmit,
  initialValues,
  onCancelClick
}) => {
  const dispatch = useDispatch()

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const { upload } = useFileUpload()

  const { showModal } = useShowModal({ modalType: Modals.IMAGE_CROPPER_MODAL })

  const [avatar, setAvatar] = React.useState<Avatar | null>(null)

  const currentUser = useCurrentUser()

  const isAdmin = currentUser.roles?.includes(UserRoles.SkeftoAdmin)

  const onAvatarUpload = (files) => {
    const file = files[0]

    if (!file) {
      return
    }

    const reader = new FileReader()

    reader.onload = () => {
      const fileAsBinaryString = reader.result

      showModal({
        width: 'md',
        image: fileAsBinaryString,
        handleCropImage: (blob: Blob, url: string) => setAvatar({ blob, url, type: file.type, changed: true })
      })
    }

    reader.onabort = console.log
    reader.onerror = console.log

    reader.readAsDataURL(files[0])
  }

  const handleFormSubmit = async (data) => {
    try {
      const additionalData: any = {}

      if (avatar?.changed) {
        const downloadUrl = await upload(avatar.blob, {
          file: getFileParams({
            assetType: AssetType.Avatar,
            contentType: avatar.type,
            id: currentUser.id
          })
        })

        additionalData.avatarUrl = downloadUrl
      }

      await updateUser({
        ...omit(data, 'totpEnabled'),
        ...additionalData,
        active: !!data.active
      })

      if (currentUser.id === initialValues.id) {
        dispatch(
          setCurrentUser({
            ...currentUser,
            firstName: data.firstName,
            lastName: data.lastName,
            ...additionalData
          })
        )
      }
    } catch (error) {
      console.log('[UserInformationForm.handleFormSubmit]: ', parseGraphQLErrorMessages(error))

      enqueueSnackbar(parseGraphQLErrorMessages(error), {
        variant: 'warning',
        autoHideDuration: null,
        action: (key) => getSnackbarCloseButton(key, closeSnackbar)
      })
    }
  }

  return (
    <>
      <Form onSubmit={handleSubmit(handleFormSubmit)}>
        <FormInnerWrapper>
          <UserData avatar={avatar?.url || initialValues.avatarUrl} onAvatarUpload={onAvatarUpload} />
          <OrganizationData />
          <SecurityData />
          <TwoFactorSettings isAdmin={isAdmin} userTotpEnabled={initialValues.totpEnabled} />
          {isAdmin && <TokenConfiguration />}
        </FormInnerWrapper>

        <BottomPaper square>
          <Grid container spacing={2} alignItems={'flex-end'}>
            <Grid>
              <Button type='submit' layout={BUTTON_LAYOUTS.PURPLE}>
                Save
              </Button>
            </Grid>
            <Grid>
              <Button layout={BUTTON_LAYOUTS.WHITE} type='button' onClick={onCancelClick}>
                Cancel
              </Button>
            </Grid>
          </Grid>
        </BottomPaper>
      </Form>
      <ImageCropper />
      <PasswordChangeModal />
    </>
  )
}

export default reduxForm<any, Props>({
  form: Forms.USER_DETAILS_EDIT_FORM,
  enableReinitialize: true
})(UserInformationForm)
